import React from 'react';
import { Users, Briefcase } from 'lucide-react';
import AnimateInView from '@/components/ui/animate-in-view';
interface ProcessStepProps {
  number: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}
const processSteps: ProcessStepProps[] = [{
  number: "01",
  title: "Confidential Discovery",
  description: "A private consultation to understand your security needs, assets, and specific concerns. We prioritize discretion from the very first conversation.",
  icon: <Users className="h-6 w-6 text-forti-fi" />
}, {
  number: "02",
  title: "Discreet Site Evaluation",
  description: "Our security experts conduct an unobtrusive assessment of your property, identifying vulnerabilities while respecting your privacy and routine.",
  icon: <Briefcase className="h-6 w-6 text-forti-fi-teal" />
}, {
  number: "03",
  title: "Bespoke Solution Design",
  description: "We craft a customized security strategy that integrates seamlessly with your property's aesthetics and your lifestyle requirements.",
  icon: <Users className="h-6 w-6 text-gold" />
}, {
  number: "04",
  title: "Precision Implementation",
  description: "Our specialized installation teams work efficiently and discreetly, minimizing disruption while ensuring optimal system performance.",
  icon: <Briefcase className="h-6 w-6 text-forti-fi" />
}];

/**
 * @component HNWIProcess
 * @description Component showcasing the premium client onboarding process
 * @returns {JSX.Element} A timeline-based section detailing the client journey
 */
const HNWIProcess: React.FC = () => {
  return <section aria-labelledby="process-heading" className="section bg-dark-bg text-white py-[75px]">
      <div className="container mx-auto">
        <AnimateInView animation="fade-in">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <div className="gold-divider mx-auto"></div>
            </div>
            <h2 id="process-heading" className="text-3xl md:text-4xl tracking-tight text-gold mb-6">
              Our Approach
            </h2>
            <p className="text-center max-w-2xl mx-auto mb-12 text-white/80">
              A refined process designed for discerning clients who value both security and discretion
            </p>
          </div>
        </AnimateInView>
        
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-px bg-forti-fi-lavender/20 hidden md:block"></div>
          
          {/* Process steps */}
          <div className="space-y-12 relative">
            {processSteps.map((step, index) => <div key={step.number} className="relative" aria-labelledby={`step-${step.number}`}>
                <AnimateInView animation={index % 2 === 0 ? "slide-right" : "slide-left"} delay={index * 100}>
                  {/* Step number marker */}
                  <div className="md:absolute md:left-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 z-10 mb-4 md:mb-0">
                    <div className="w-12 h-12 rounded-full bg-dark-bg flex items-center justify-center border-2 border-gold">
                      <span className="text-gold font-serif">{step.number}</span>
                    </div>
                  </div>
                  
                  {/* Step content */}
                  <div className={`md:w-5/12 ${index % 2 === 0 ? 'md:mr-auto' : 'md:ml-auto'} bg-deep-purple/30 p-6 rounded-lg border border-forti-fi-lavender/10 shadow-luxury`}>
                    <div className="flex items-center mb-4">
                      <div className="mr-3 p-2 rounded-full bg-forti-fi-lavender/10">
                        {step.icon}
                      </div>
                      <h3 id={`step-${step.number}`} className="text-xl font-serif text-gold">{step.title}</h3>
                    </div>
                    <p className="text-white/80">{step.description}</p>
                  </div>
                </AnimateInView>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};
export default HNWIProcess;